import React from 'react'
import { useTranslation } from 'react-i18next'
import { Crown } from 'lucide-react'

/**
 * Анимация жеребьёвки недельного розыгрыша (state = drawing).
 * Участников может быть сотни — показывать их аватарки бессмысленно,
 * поэтому вокруг приза крутится зацикленная «орбита»: вращающееся
 * золотое кольцо, расходящиеся волны и орбитальные искры.
 * Длительность стадии не гарантирована (воркер ходит раз в 60 сек),
 * поэтому анимация бесконечная — крутится, пока бэкенд не пришлёт
 * state = results, тогда страница сама переключит экран.
 */
const RushDropWeeklyDrawing = ({ skin }) => {
  const { t } = useTranslation()

  return (
    <div className="flex flex-col items-center gap-6 rounded-2xl border border-yellow-400/30 bg-gradient-to-br from-yellow-400/10 to-orange-500/5 px-4 py-10">
      {/* Сцена с ножом в центре и анимацией вокруг */}
      <div className="relative flex h-56 w-56 items-center justify-center">
        {/* Расходящиеся волны — три кольца с задержкой друг за другом */}
        <span className="weekly-draw-pulse absolute inset-0 rounded-full border border-yellow-400/40" style={{ animation: 'weeklyDrawPulse 2.4s ease-out infinite' }} />
        <span className="weekly-draw-pulse absolute inset-0 rounded-full border border-yellow-400/40" style={{ animation: 'weeklyDrawPulse 2.4s ease-out infinite 0.8s' }} />
        <span className="weekly-draw-pulse absolute inset-0 rounded-full border border-yellow-400/40" style={{ animation: 'weeklyDrawPulse 2.4s ease-out infinite 1.6s' }} />

        {/* Вращающееся золотое кольцо (conic-gradient) */}
        <span
          className="weekly-draw-spin absolute inset-6 rounded-full"
          style={{
            background: 'conic-gradient(from 0deg, transparent 0deg, rgba(250,204,21,0.0) 30deg, rgba(250,204,21,0.85) 90deg, rgba(255,165,0,0.85) 150deg, transparent 210deg, transparent 360deg)',
            mask: 'radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 3px))',
            WebkitMask: 'radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 3px))',
            animation: 'weeklyDrawSpin 3s linear infinite'
          }}
        />

        {/* Орбитальные искры — точки на двух орбитах с разной скоростью */}
        <span
          className="weekly-draw-orbit absolute inset-2"
          style={{ animation: 'weeklyDrawOrbit 4s linear infinite' }}
        >
          <span className="absolute left-1/2 top-0 h-2 w-2 -translate-x-1/2 rounded-full bg-yellow-300 shadow-[0_0_8px_rgba(250,204,21,0.8)]" />
        </span>
        <span
          className="weekly-draw-orbit absolute inset-0"
          style={{ animation: 'weeklyDrawOrbit 6s linear infinite reverse' }}
        >
          <span className="absolute left-1/2 top-0 h-1.5 w-1.5 -translate-x-1/2 rounded-full bg-orange-400 shadow-[0_0_6px_rgba(251,146,60,0.8)]" />
        </span>

        {/* Приз в центре — «дышит» золотым свечением */}
        <div
          className="weekly-draw-breath relative flex h-28 w-28 items-center justify-center"
          style={{ animation: 'weeklyDrawBreath 2s ease-in-out infinite' }}
        >
          {skin?.image ? (
            <img
              src={skin.image}
              alt={skin.name || ''}
              className="h-28 w-28 object-contain"
              loading="lazy"
            />
          ) : (
            <div className="flex h-28 w-28 items-center justify-center rounded-full bg-white/5">
              <Crown size={40} className="text-yellow-400/60" />
            </div>
          )}
        </div>
      </div>

      {/* Подпись статуса */}
      <div className="flex flex-col items-center gap-1.5 text-center">
        <p className="text-sm font-bold uppercase tracking-wider text-yellow-300">
          {t('rushdrop.weeklyDraw.drawing', 'Идёт розыгрыш')}
        </p>
        <p className="text-xs text-text-secondary">
          {t('rushdrop.weeklyDraw.drawingHint', 'Выбираем победителя — страница обновится сама')}
        </p>
      </div>
    </div>
  )
}

export default RushDropWeeklyDrawing
